import { useEffect } from 'react';

/**
 * Global keymap for the reader. Bound on `window` so the user can navigate
 * even when no specific element has focus (which is the common case while
 * scrolling).
 *
 * # Keymap
 *
 *   ←  / a / k / PageUp        previous page
 *   →  / d / j / Space / PageDown  next page
 *   Home                       first page
 *   End                        last page
 *   f                          cycle fit modes (width → height → free → width)
 *   m                          cycle reader modes (vertical → horizontal → spread)
 *   r                          toggle read direction (LTR ↔ RTL)
 *   Escape                     close / go back (handler decides)
 *   ?                          show the help overlay
 *
 * Keys that have native browser semantics on text inputs (e.g. Space scrolling
 * a textarea) are skipped when the active element is editable. The reader's
 * text inputs are typically the page-jump field in the toolbar.
 */

export interface KeyboardNavHandlers {
  onPrev: () => void;
  onNext: () => void;
  onFirst?: () => void;
  onLast?: () => void;
  onCycleFit?: () => void;
  onCycleMode?: () => void;
  onToggleDirection?: () => void;
  onClose?: () => void;
  onHelp?: () => void;
}

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return t.isContentEditable;
}

export function useKeyboardNav(handlers: KeyboardNavHandlers, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'k':
        case 'PageUp':
          handlers.onPrev();
          e.preventDefault();
          break;
        case 'ArrowRight':
        case 'd':
        case 'j':
        case 'PageDown':
        case ' ': // Space
          handlers.onNext();
          e.preventDefault();
          break;
        case 'Home':
          handlers.onFirst?.();
          e.preventDefault();
          break;
        case 'End':
          handlers.onLast?.();
          e.preventDefault();
          break;
        case 'f':
        case 'F':
          handlers.onCycleFit?.();
          e.preventDefault();
          break;
        case 'm':
        case 'M':
          handlers.onCycleMode?.();
          e.preventDefault();
          break;
        case 'r':
        case 'R':
          handlers.onToggleDirection?.();
          e.preventDefault();
          break;
        case 'Escape':
          handlers.onClose?.();
          break;
        case '?':
          handlers.onHelp?.();
          e.preventDefault();
          break;
        default:
          break;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, handlers]);
}

export const READER_KEYMAP_HELP: Array<{ keys: string; label: string }> = [
  { keys: '← / A / K / PgUp', label: 'Previous page' },
  { keys: '→ / D / J / Space / PgDn', label: 'Next page' },
  { keys: 'Home / End', label: 'First / last page' },
  { keys: 'F', label: 'Cycle fit (width → height → free)' },
  { keys: 'M', label: 'Cycle mode (vertical → horizontal → spread)' },
  { keys: 'R', label: 'Toggle direction (LTR / RTL)' },
  { keys: 'Esc', label: 'Close reader' },
  { keys: '?', label: 'Show this help' },
];
